/**
 * 页面能力注册表。
 * - 页面挂载并加载完成后注册能力（含 ready/buildContext/execute）
 * - 页面卸载时注销
 * - whenReady(pageId) 供命令队列等待页面与图层就绪（替代 waitForMapView 轮询）
 */
import type { AgentCapability, AgentUiCommand } from "@/agent/types";

type ReadyListener = () => void;

const POLL_INTERVAL_MS = 50;

export class AgentCapabilityRegistry {
  private capabilities = new Map<string, AgentCapability>();
  private readyFlags = new Map<string, boolean>();
  private listeners = new Map<string, Set<ReadyListener>>();

  register(capability: AgentCapability): void {
    this.capabilities.set(capability.pageId, capability);
    this.notify(capability.pageId);
  }

  unregister(pageId: string, capability?: AgentCapability): void {
    if (capability && this.capabilities.get(pageId) !== capability) return;
    this.capabilities.delete(pageId);
    this.readyFlags.delete(pageId);
    this.notify(pageId);
  }

  get(pageId: string): AgentCapability | undefined {
    return this.capabilities.get(pageId);
  }

  getAll(): AgentCapability[] {
    return [...this.capabilities.values()];
  }

  /** 页面显式通知就绪状态变化（例如地图 onReady、数据加载完成） */
  setReady(pageId: string, ready: boolean): void {
    this.readyFlags.set(pageId, ready);
    this.notify(pageId);
  }

  isReady(pageId: string): boolean {
    const capability = this.capabilities.get(pageId);
    if (!capability) return false;
    const flag = this.readyFlags.get(pageId);
    if (flag !== undefined) {
      return flag && capability.ready();
    }
    return capability.ready();
  }

  listRegistered(): string[] {
    return [...this.capabilities.keys()];
  }

  /** 等待页面注册且就绪；超时返回 null */
  whenReady(pageId: string, timeoutMs = 12000): Promise<AgentCapability | null> {
    return new Promise((resolve) => {
      const deadline = Date.now() + timeoutMs;
      const check = (): void => {
        if (this.isReady(pageId)) {
          resolve(this.capabilities.get(pageId) ?? null);
          return;
        }
        if (Date.now() >= deadline) {
          resolve(null);
          return;
        }
        setTimeout(check, POLL_INTERVAL_MS);
      };
      const onChanged = (): void => {
        if (this.isReady(pageId)) {
          this.off(pageId, onChanged);
          resolve(this.capabilities.get(pageId) ?? null);
        }
      };
      this.on(pageId, onChanged);
      check();
    });
  }

  /** 按命令类型的前缀解析目标页面 id（app.* 由 AppCommandHandler 处理，返回 null） */
  pageIdForCommand(command: AgentUiCommand): string | null {
    if (command.type.startsWith("map.")) return "map";
    if (command.type.startsWith("tour.")) return "map";
    if (command.type.startsWith("building.")) return "map";
    if (command.type.startsWith("nebula.")) return "nebula";
    if (command.type.startsWith("photos.")) return "old-photos";
    if (command.type.startsWith("archive.")) return "building-detail";
    if (command.type.startsWith("contribution.")) return "crowdsource-submit";
    if (command.type.startsWith("analysis.")) return "memory-grid-insight";
    if (command.type.startsWith("growth.")) return "growth";
    return null;
  }

  private on(pageId: string, listener: ReadyListener): void {
    let set = this.listeners.get(pageId);
    if (!set) {
      set = new Set();
      this.listeners.set(pageId, set);
    }
    set.add(listener);
  }

  private off(pageId: string, listener: ReadyListener): void {
    const set = this.listeners.get(pageId);
    if (set) {
      set.delete(listener);
    }
  }

  private notify(pageId: string): void {
    const set = this.listeners.get(pageId);
    if (set) {
      for (const listener of [...set]) {
        listener();
      }
    }
  }
}
